import React, { useCallback } from 'react';
import { Tabs, Tab, Button, IconButton, Tooltip, CircularProgress, Typography, Chip } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import FilterListIcon from '@mui/icons-material/FilterList';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import FlightIcon from '@mui/icons-material/Flight';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import SpaIcon from '@mui/icons-material/Spa';
import BugReportIcon from '@mui/icons-material/BugReport';

import FilterChipGroup from './FilterChipGroup';
import { FilterBarWrapper, TopRow, TabsWrapper, ActionsGroup, FiltersGroup } from './DashboardFilterBarStyle';

// ── Activity definitions ──
const ACTIVITIES = [
  { value: 'COSECHA_MECANICA',    label: 'Cosecha',    icon: <AgricultureIcon fontSize="small" /> },
  { value: 'APLICACIONES_AEREAS', label: 'Aéreas',     icon: <FlightIcon fontSize="small" /> },
  { value: 'FERTILIZACION',       label: 'Fertilización', icon: <SpaIcon fontSize="small" /> },
  { value: 'DETECCION_MALEZA',    label: 'Maleza',     icon: <BugReportIcon fontSize="small" /> },
  { value: 'HERBICIDAS',          label: 'Herbicidas', icon: <LocalFloristIcon fontSize="small" /> },
  { value: 'SIEMBRA',             label: 'Siembra',    icon: <SpaIcon fontSize="small" /> },
];

// ── Filter chip definitions ──
const FILTER_KEYS = [
  { key: 'operadores',   label: 'Operador' },
  { key: 'responsables', label: 'Responsable' },
  { key: 'fincas',       label: 'Finca' },
  { key: 'zafras',       label: 'Zafra' },
  { key: 'tercios',      label: 'Tercio' },
  { key: 'lotes',        label: 'Lote' },
  { key: 'productos',    label: 'Producto' },
  { key: 'equipos',      label: 'Equipo' },
  { key: 'tiposProducto', label: 'Tipo de producto' },
  { key: 'usuarios',     label: 'Usuario' },
];

/**
 * DashboardFilterBar — sticky bar with activity tabs, date range, filter chips, and reset.
 *
 * Props:
 *   selectedActivity   — string (current activity id)
 *   onActivityChange   — (activityId: string) => void
 *   dateRange          — { start: moment, end: moment }
 *   onDateRangeChange  — ({ start, end }) => void
 *   filterOptions      — { operadores: string[], fincas: string[], ... }
 *   selectedFilters    — { operadores: string[], fincas: string[], ... }
 *   onFiltersChange    — (filters) => void
 *   onReset            — () => void
 *   onApplyFilters     — () => void (triggers data fetch with current filter selection)
 *   onGenerateReport   — () => void (optional: shows PDF button when provided)
 *   isGeneratingReport — boolean (optional: disables PDF button while generating)
 *   totalAnalisisCount — number (optional: displays total analysis badge)
 *   isViewer           — boolean (optional: shows read-only chip)
 *   extraContent       — ReactNode (optional: additional elements like analyst selector)
 */
const DashboardFilterBar = ({
  selectedActivity,
  onActivityChange,
  dateRange,
  onDateRangeChange,
  filterOptions = {},
  selectedFilters = {},
  onFiltersChange,
  onReset,
  onApplyFilters,
  onGenerateReport,
  isGeneratingReport = false,
  totalAnalisisCount,
  isViewer = false,
  extraContent,
  hiddenCount = 0,
  onVisibilityClick,
}) => {
  const handleActivityTab = useCallback(
    (_, newValue) => onActivityChange(newValue),
    [onActivityChange]
  );

  const handleFilterChange = useCallback(
    (key, newValues) => {
      onFiltersChange({ ...selectedFilters, [key]: newValues });
    },
    [selectedFilters, onFiltersChange]
  );

  // Show all filter chips that are defined for the current activity (even if empty)
  const activeFilterKeys = FILTER_KEYS.filter((f) => f.key in filterOptions);

  return (
    <FilterBarWrapper>
      {/* ─── Row 1: Tabs + Dates + Actions ─── */}
      <TopRow>
        <TabsWrapper>
          <Tabs
            value={selectedActivity}
            onChange={handleActivityTab}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 36,
              '& .MuiTab-root': {
                borderRadius: '20px',
                minHeight: 36,
                textTransform: 'none',
                fontSize: 13,
                fontWeight: 500,
                paddingX: 2,
              },
              '& .Mui-selected': {
                backgroundColor: 'primary.light',
                color: 'primary.dark',
              },
              '& .MuiTabs-indicator': {
                display: 'none',
              },
            }}
          >
            {ACTIVITIES.map((a) => (
              <Tab
                key={a.value}
                value={a.value}
                icon={a.icon}
                iconPosition="start"
                label={a.label}
              />
            ))}
          </Tabs>
        </TabsWrapper>

        {/* Date Range */}
        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="es">
          <DatePicker
            label="Desde"
            value={dateRange?.start || null}
            onChange={(val) => onDateRangeChange({ ...dateRange, start: val })}
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: 150, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 13 } },
              },
            }}
          />
          <DatePicker
            label="Hasta"
            value={dateRange?.end || null}
            onChange={(val) => onDateRangeChange({ ...dateRange, end: val })}
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: 150, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 13 } },
              },
            }}
          />
        </LocalizationProvider>

        {/* Actions pushed to the right */}
        <ActionsGroup>
          {onApplyFilters && (
            <Tooltip title="Aplicar filtros">
              <Button
                variant="contained"
                size="small"
                startIcon={<FilterListIcon />}
                onClick={onApplyFilters}
                sx={{ borderRadius: '8px', textTransform: 'none', fontSize: 13 }}
              >
                Filtrar
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Restablecer filtros">
            <Button
              variant="outlined"
              size="small"
              startIcon={<RestartAltIcon />}
              onClick={onReset}
              sx={{ borderRadius: '8px', textTransform: 'none', fontSize: 13 }}
            >
              Reset
            </Button>
          </Tooltip>
          {onGenerateReport && (
            <Tooltip title="Descargar reporte PDF">
              <IconButton
                onClick={onGenerateReport}
                disabled={isGeneratingReport}
                size="small"
                sx={{ color: isGeneratingReport ? 'text.disabled' : 'error.main' }}
              >
                {isGeneratingReport ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
              </IconButton>
            </Tooltip>
          )}
          {onVisibilityClick && (
            <Tooltip title="Configurar indicadores visibles" arrow>
              <IconButton
                size="small"
                onClick={onVisibilityClick}
                sx={{
                  color: hiddenCount > 0 ? '#ff9900' : '#64748b',
                  '&:hover': { backgroundColor: 'rgba(255,153,0,0.08)' },
                  display: 'flex', alignItems: 'center', gap: 0.5,
                }}
              >
                <VisibilityIcon fontSize="small" />
                {hiddenCount > 0 && (
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 11, lineHeight: 1 }}>
                    {hiddenCount}
                  </Typography>
                )}
              </IconButton>
            </Tooltip>
          )}
          {totalAnalisisCount != null && (
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              Total análisis: <b>{totalAnalisisCount}</b>
            </Typography>
          )}
          {isViewer && <Chip size="small" label="Solo lectura" sx={{ fontWeight: 600 }} />}
          {extraContent}
        </ActionsGroup>
      </TopRow>

      {/* ─── Row 2: Filter dropdowns (grid) ─── */}
      {activeFilterKeys.length > 0 && (
        <FiltersGroup>
          {activeFilterKeys.map((f) => (
            <FilterChipGroup
              key={f.key}
              label={f.label}
              options={filterOptions[f.key] || []}
              value={selectedFilters[f.key] || []}
              onChange={(vals) => handleFilterChange(f.key, vals)}
            />
          ))}
        </FiltersGroup>
      )}
    </FilterBarWrapper>
  );
};

export default React.memo(DashboardFilterBar);
